import React, { useCallback, useMemo, useState, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DocumentIcon from 'images/document-gray.svg';
import CalendarIcon from 'images/calendar.svg';
import { Status, Label, Value } from './styles';
import { getFormattedDate, getMoney } from 'utils/transform';
import {
  Divider,
  Grid,
  useMediaQuery,
  Button,
  CircularProgress,
  Tooltip } from '@mui/material';
import { When, Else, If, Then } from 'react-if';
import ParcelaItem from './ParcelaItem';
import { PARCELA_EM_ABERTO } from 'utils/constants';
import { useAuth } from 'contexts/auth';
import api from 'services/api';
import { IAcordo } from 'interfaces/Acordo';
import { ICustomizacao } from 'interfaces/Customizacao';
import { buscaCustomService } from 'services/buscaCustomizacao/buscaCustomizacao';
import { IDivida } from 'interfaces/Divida';
import { useNavigate } from 'react-router-dom';

interface IDividaItemProps {
  item: IAcordo;
  itemDivida: IDivida;
  onClickRegularizar: any;
  permiteBaixarBoleto: boolean;
  possuiParametrosBoleto: boolean;
}

function DividaAcordoItem({
  item,
  onClickRegularizar,
  permiteBaixarBoleto,
  possuiParametrosBoleto,
  itemDivida
}: IDividaItemProps) {
  const [expanded, setExpanded] = useState(false);
  const isDesktop = useMediaQuery((theme: any) => theme?.breakpoints?.up('sm'));
  const isMobile = useMediaQuery((th: any) => th?.breakpoints?.down('sm'));
  const [loadingCancelar, setLoadingCancelar] = useState(false);
  const { bank, user } = useAuth();
  const [error, setError] = useState<Error>();
  const navigate = useNavigate()

  const [custom, setCustom] = useState<ICustomizacao>();

  const parcelasPendentes = useMemo(() => {
    return (
      item?.parcelas?.filter(
        parcela => parcela?.status === PARCELA_EM_ABERTO,
      ) || []
    );
  }, [item]);

  const proximoPagamento = useMemo(() => {
    const proximaParcela = Math.min.apply(
      Math,
      parcelasPendentes?.map(parcela =>
        Number(parcela?.numeroParcela?.split('/')[0]),
      ),
    );

    return item?.parcelas?.find(
      parcela =>
        Number(parcela?.numeroParcela?.split('/')[0]) === proximaParcela,
    );
  }, [parcelasPendentes, item]);

  const formattedParcelas = useMemo(() => {
    const pendentes = parcelasPendentes.map(parcela => parcela?.numeroParcela);

    if (!pendentes.length) return '-';

    if (pendentes.length === 1) {
      return pendentes[0];
    }

    return `${pendentes.slice(0, pendentes.length - 1).join(', ')} e ${pendentes[pendentes.length - 1]
      }`;
  }, [parcelasPendentes]);

  const handleCancelar = useCallback(
    async (idAcordo: string | number | undefined, crm?: number | string) => {
      setLoadingCancelar(true);
      try {
        const { data } = await api.post('cancela-acordo', {
          idAcordo,
          crm: crm || (!!bank?.crms && bank?.crms[0]) || 1,
          carteiraId: item.codCarteira
        });
        if (!!data?.sucessoField) {
          setLoadingCancelar(false);
          setTimeout(() => {
            setLoadingCancelar(false);
            navigate('/dashboard');
          }, 2000);
        }
      } catch (err) {
        setLoadingCancelar(false);
        setError(err as Error);
      } finally {
        setLoadingCancelar(false);
      }
    },
    [setLoadingCancelar, setError, bank, error],
  );

  useEffect(() => {
    async function buscaCustomizacao() {
      const { data: newCustom } = await buscaCustomService({
        crms: item?.crm,
        carteiraId: item.codCarteira,
      })
      setCustom(newCustom);
    }
    buscaCustomizacao();
  }, []);

  return (
    <Box mb={2}>
      <Accordion
        expanded={expanded}
        onChange={(_event, newValue) => setExpanded(newValue)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
          <Box width="100%">
            <Box display="flex" alignItems="center">
              <Box mr={2}>
                <Status color="secondary">
                  <strong>Dívida negociada</strong>
                </Status>
              </Box>
              <Box mr={1}>
                <img
                  style={{ marginBottom: 2 }}
                  src={DocumentIcon}
                  alt="Documento"
                />
              </Box>
              <Status>
                <span>
                  {Number(item?.contrato?.length)} Contrato
                  {Number(item?.contrato?.length) > 1 && 's'}
                </span>
              </Status>
            </Box>
            <Box>
              {
                custom?.ImagemCarteira !== '' && !!custom?.ImagemCarteira ?
                  <img
                    width='50px'
                    src={custom?.ImagemCarteira}>
                  </img>
                  :
                  <Typography style={{ fontSize: 28 }} variant="h6">
                    <strong>{bank?.financeira}</strong>
                  </Typography>
              }
            </Box>
            <When condition={!expanded}>
              <Box mt={2}>
                <Grid container>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Label>Próximo pagamento</Label>
                      <Value regular>
                        {!!proximoPagamento?.valorEntrada
                          ? 'Entrada'
                          : !!proximoPagamento?.numeroParcela
                            ? `Pagamento ${proximoPagamento?.numeroParcela}`
                            : '-'}
                      </Value>
                    </Box>
                  </Grid>
                  {!!proximoPagamento?.dataVencimento && (
                    <Grid item xs={6} sm={4}>
                      <Box>
                        <Label>Vencimento</Label>
                        <Value regular>
                          {getFormattedDate(proximoPagamento?.dataVencimento)}
                        </Value>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Label>Valor</Label>
                      <Value regular>
                        {!!proximoPagamento?.valorEntrada
                          ? getMoney(proximoPagamento.valorEntrada)
                          : getMoney(proximoPagamento?.valor)}
                      </Value>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </When>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <When condition={expanded}>
              <Grid container>
                <Grid item xs={12} sm={12} spacing={4}>
                  <Box mb={2}>
                    <Grid container alignItems="center">
                      {!!item?.dataNegociacao && item?.valor !== 0 && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            <Label>Data da negociação</Label>
                            <Value regular>
                              {getFormattedDate(item?.dataNegociacao)}</Value>
                          </Box>
                        </Grid>
                      )}
                      {!!item?.valor && item?.valor !== 0 && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            {' '}
                            <Label>Valor do acordo</Label>
                            <Value regular>{getMoney(item?.valor)}</Value>
                          </Box>
                        </Grid>
                      )}
                      {!!item?.valorDivida && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            <Label>
                              {custom && (
                                custom.DividaAcordoItem
                              )}
                            </Label>
                            <Value regular>
                              {getMoney(item?.valorDivida)}
                            </Value>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                  <Box mb={2}>
                    <Grid container alignItems="center">
                      {!!proximoPagamento?.valorEntrada && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            <Label>Valor da entrada</Label>
                            <Value regular>
                              {getMoney(proximoPagamento?.valorEntrada)}
                            </Value>
                          </Box>
                        </Grid>
                      )}
                      {(!!item.valorEntrada && !proximoPagamento?.valorEntrada) && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            <Label>Valor da entrada</Label>
                            <Value regular>
                              {getMoney(item?.valorEntrada)}
                            </Value>
                          </Box>
                        </Grid>
                      )}
                      {!!item?.valorParcela && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            <Label>Valor das parcelas</Label>
                            <Value regular>
                              {getMoney(item?.valorParcela)}
                            </Value>
                          </Box>
                        </Grid>
                      )}
                      {!!proximoPagamento?.quantidadeParcelas && proximoPagamento?.quantidadeParcelas !== 0 && (
                        <Grid item xs={6} sm={4}>
                          <Box>
                            <Label>Quantidade de parcelas</Label>
                            <Value regular>
                              {proximoPagamento?.quantidadeParcelas}
                            </Value>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={3} sm={3}>
                  <When condition={!!item?.permitirCancelamento}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      mb={1}
                      style={{ minWidth: '250px' }}
                    >
                      {
                        item?.permitirCancelamento ?
                          <Button
                            variant="contained"
                            fullWidth={true}
                            color="primary"
                            style={{
                              textTransform: 'none',
                              height: 44,
                              color: '#fff',
                            }}
                            onClick={() =>
                              handleCancelar(item?.idAcordo, item?.crm)
                            }
                            size={isMobile ? 'small' : 'medium'}
                          >
                            <If condition={loadingCancelar}>
                              <Then>
                                <Box
                                  width="100%"
                                  display="flex"
                                  justifyContent="center"
                                >
                                  <CircularProgress style={{ color: 'white' }} />
                                </Box>
                              </Then>
                              <Else>Cancelar Acordo</Else>
                            </If>
                          </Button> :
                          <Grid container>
                            {isMobile && (
                              <Grid item xs={12}>
                                <Typography style={{ fontSize: '12px' }} >O acordo só pode ser cancelado através dos canais de comunicação!</Typography>
                              </Grid>
                            )}
                            <Tooltip title="O acordo só pode ser cancelado através dos canais de comunicação!">
                              <span style={{ width: '100%' }}>
                                <Button
                                  variant="contained"
                                  fullWidth={true}
                                  color="primary"
                                  disabled
                                  style={{
                                    textTransform: 'none',
                                    height: 44,
                                    color: '#fff',
                                  }}
                                  size={isMobile ? 'small' : 'medium'}
                                >
                                  Cancelar Acordo
                                </Button>
                              </span>
                            </Tooltip>
                          </Grid>
                      }
                    </Box>
                  </When>
                </Grid>
              </Grid>
            </When>
            <Divider />
            {item?.contrato?.map((contrato, contratoIndex) => (
              <Box key={`${contrato.numero}-${contratoIndex}`} width="100%">
                <Grid container alignItems="center">
                  {!!contrato?.produto && (
                    <Grid item xs={6} sm={4}>
                      <Box>
                        <Label>Produto:</Label>
                        <Value>{contrato.produto}</Value>
                      </Box>
                    </Grid>
                  )}
                  {!!contrato?.documento && (
                    <Grid item xs={12} sm>
                      <Box>
                        <Label>Contrato:</Label>
                        <Value>{contrato.documento}</Value>
                      </Box>
                    </Grid>
                  )}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Grid item>
                      {/* <Label style={{ fontSize: 18 }}>
                        {getMoney(contrato.valor)}
                      </Label> */}
                    </Grid>
                  </Box>
                </Grid>
                <Grid container>
                  {!!contrato?.dataContratacao && (
                    <Grid item xs={6} sm={3}>
                      <Box>
                        <Label>Contratação:</Label>
                        <Value>
                          {getFormattedDate(contrato.dataContratacao)}
                        </Value>
                      </Box>
                    </Grid>
                  )}
                  {!!contrato?.dataAtraso && (
                    <Grid item xs={6} sm={3}>
                      <Box>
                        <Label>Atraso:</Label>
                        <Value>
                          {getFormattedDate(contrato.dataAtraso)}
                        </Value>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={3}>
                    {!!contrato?.diasAtraso && (
                      <Box>
                        <Label>Parcelas:</Label>
                        <Value>{formattedParcelas}</Value>
                      </Box>
                    )}
                  </Grid>
                  <When condition={Number(contrato?.diasAtraso) > 0}>
                    <Grid item xs={6} sm={3}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems={isDesktop ? 'flex-end' : 'flex-start'}
                      >
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                          <Label style={{ marginBottom: 2 }}>&nbsp;</Label>
                        </Box>

                        <Box display="flex" alignItems="flex-end">
                          <Box mr={1}>
                            <img src={CalendarIcon} alt="Calendário" />
                          </Box>
                          <Value style={{ marginBottom: 0 }}>
                            Atraso:{' '}
                            <span style={{ fontWeight: 'lighter' }}>
                              {contrato.diasAtraso} dias
                            </span>
                          </Value>
                        </Box>
                      </Box>
                    </Grid>
                  </When>
                </Grid>
                {Number(item?.contrato?.length) - 1 !== contratoIndex && (
                  <Divider />
                )}
              </Box>
            ))}
            <Box mt={2}>

              <Grid container>
                <Grid item xs={12} sm={12} md={6}>
                  {!possuiParametrosBoleto && (
                    <Typography
                      gutterBottom
                      style={{ fontSize: 12, fontWeight: 500, maxWidth: 450, color: 'red' }}
                    >
                      Prezado cliente, identificamos um acordo vigente para seus contratos. Duvidas entrar em contato com nossos canais de atendimento:
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <Box style={{ display: "flex", justifyContent: isMobile ? "flex-start" : "flex-end", marginTop: isMobile ? "6px" : "0" }}>
                    {!possuiParametrosBoleto && (
                      <Button
                        style={{ color: '#fff', whiteSpace: 'nowrap' }}
                        color="secondary"
                        variant="contained"
                        onClick={onClickRegularizar}
                      >
                        Canais de atendimento
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Label>Parcelas</Label>
                <Grid container spacing={1}>
                  {item?.parcelas?.map((parcelaItem, index) => (
                    <Grid key={index} item xs={12}>
                      <ParcelaItem item={parcelaItem} acordo={item} custom={custom} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default DividaAcordoItem;
